import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X, AlertCircle } from "lucide-react";

/**
 * SearchableSelect — single-value autocomplete dropdown.
 *
 * - Typing instantly filters by `case-insensitive substring` on the option label.
 * - Arrow keys + Enter navigate / commit selection.
 * - "No results found" empty state.
 * - Accepts `options` as Array<{ value, label?, hint? }>.
 *
 * Props:
 *   options       Array of options
 *   value         currently selected value (string)
 *   onChange      (newValue, optionObject?) => void
 *   placeholder   Placeholder text
 *   disabled
 *   allowClear    Show ✕ to clear
 *   loading       Optional spinner state
 *   testid        Base test-id, e.g. "cdl-country"
 *   minChars      Don't filter until user types this many chars (default 0)
 *   inputClassName  Tailwind override for the input
 *   required      Visual hint only — caller is responsible for actual validation
 */
export default function SearchableSelect({
    options = [],
    value = "",
    onChange,
    placeholder = "Type to search…",
    disabled = false,
    allowClear = true,
    loading = false,
    testid = "ss",
    minChars = 0,
    inputClassName = "",
    required = false,
}) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);
    const listRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value]);

    const filtered = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return options;
        if (qq.length < minChars) return options;
        return options.filter((o) => {
            const hay = `${o.label ?? o.value}`.toLowerCase();
            return hay.includes(qq);
        });
    }, [options, q, minChars]);

    // Keep highlight in range when filtered list shrinks
    useEffect(() => {
        if (highlight >= filtered.length) setHighlight(Math.max(0, filtered.length - 1));
    }, [filtered, highlight]);

    const commit = (opt) => {
        if (!opt) return;
        onChange?.(opt.value, opt);
        setOpen(false);
        setQ("");
    };

    const onKey = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            setOpen(true);
            e.preventDefault();
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(filtered.length - 1, h + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlight]) commit(filtered[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    const inputCls = `w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] disabled:opacity-50 disabled:cursor-not-allowed ${inputClassName}`;

    return (
        <div ref={wrapRef} className="relative" data-testid={`${testid}-wrap`}>
            <div className="relative">
                {open ? (
                    <>
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setHighlight(0); }}
                            onKeyDown={onKey}
                            placeholder={placeholder}
                            data-testid={`${testid}-search`}
                            className={`${inputCls} pl-9 pr-8`}
                        />
                        <button
                            type="button"
                            onClick={() => { setOpen(false); setQ(""); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                            data-testid={`${testid}-close`}
                            tabIndex={-1}
                        >
                            <X className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setOpen(true)}
                        onKeyDown={onKey}
                        data-testid={`${testid}-trigger`}
                        data-empty={value ? undefined : "true"}
                        data-required={required ? "true" : undefined}
                        className={`${inputCls} flex items-center justify-between text-left`}
                    >
                        <span className={selectedOption ? "text-slate-900" : "text-slate-400"}>
                            {selectedOption ? (selectedOption.label || selectedOption.value) : placeholder}
                        </span>
                        <ChevronDown className="w-4 h-4 text-slate-400 ml-2 shrink-0" />
                    </button>
                )}
                {!open && value && allowClear && !disabled && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onChange?.("", null); }}
                        className="absolute right-7 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                        data-testid={`${testid}-clear`}
                        tabIndex={-1}
                        title="Clear"
                    >
                        <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                )}
            </div>

            {open && (
                <div
                    ref={listRef}
                    className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
                    data-testid={`${testid}-options`}
                >
                    {loading && (
                        <div className="px-3 py-3 text-xs text-slate-500">Loading…</div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid={`${testid}-empty`}>
                            <AlertCircle className="w-3.5 h-3.5" /> No results found
                        </div>
                    )}
                    {!loading && filtered.map((o, i) => (
                        <button
                            key={o.value}
                            type="button"
                            onMouseEnter={() => setHighlight(i)}
                            onClick={() => commit(o)}
                            data-testid={`${testid}-opt-${String(o.label || o.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            data-active={i === highlight ? "true" : undefined}
                            className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition ${
                                i === highlight ? "bg-blue-50 text-blue-900" : "hover:bg-slate-50"
                            } ${o.value === value ? "font-semibold" : ""}`}
                        >
                            <span>{o.label || o.value}</span>
                            {o.hint && <span className="ml-2 text-[10px] text-slate-400">{o.hint}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
